import {
  fillExactVideoCacheForSource,
  getVideoCacheKeyForSource,
  hasExactCachedVideoForSource,
  type VideoCachePlaybackSource,
} from './videoCache'
import {
  isTimelinePlaybackActive,
  recordPlaybackBackgroundWork,
  subscribeTimelinePlaybackActivity,
} from './playbackActivity'
import { createVideoPlaybackSourceFromUrl } from './videoPlaybackSource'

type FullFileVideoPreloadReason = 'generation-complete' | 'upload-complete' | 'proxy-ready' | 'manual'

type QueuedFullFileVideoPreload = {
  source: VideoCachePlaybackSource
  reason: FullFileVideoPreloadReason
  queuedAt: number
  attempts: number
}

type InFlightFullFileVideoPreload = {
  item: QueuedFullFileVideoPreload
  controller: AbortController
}

const IDLE_START_DELAY_MS = 3_000
const PLAYBACK_RETRY_DELAY_MS = 1_500
const FAILURE_RETRY_DELAY_MS = 10_000
const MAX_ATTEMPTS = 2

const queue = new Map<string, QueuedFullFileVideoPreload>()
let scheduledTimer: ReturnType<typeof setTimeout> | null = null
let scheduledIdleCallback: number | null = null
let inFlight: InFlightFullFileVideoPreload | null = null
let playbackActive = isTimelinePlaybackActive()

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

function isDocumentVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden'
}

function canStartFullFilePreload(): boolean {
  return isBrowser() && !playbackActive && !isTimelinePlaybackActive() && isDocumentVisible()
}

function clearScheduledWork(): void {
  if (scheduledTimer) {
    clearTimeout(scheduledTimer)
    scheduledTimer = null
  }
  if (scheduledIdleCallback && isBrowser() && 'cancelIdleCallback' in window) {
    window.cancelIdleCallback(scheduledIdleCallback)
    scheduledIdleCallback = null
  }
}

function scheduleFullFilePreload(delayMs = IDLE_START_DELAY_MS): void {
  if (!isBrowser() || scheduledTimer || scheduledIdleCallback || inFlight || queue.size === 0) return

  scheduledTimer = setTimeout(() => {
    scheduledTimer = null
    if (!canStartFullFilePreload()) {
      if (playbackActive || isTimelinePlaybackActive()) {
        recordPlaybackBackgroundWork('full-file-video-preload', 'deferred', {
          reason: 'timeline-playback-active',
          count: queue.size,
        })
      }
      scheduleFullFilePreload(PLAYBACK_RETRY_DELAY_MS)
      return
    }

    if ('requestIdleCallback' in window) {
      scheduledIdleCallback = window.requestIdleCallback(
        deadline => {
          scheduledIdleCallback = null
          if (!canStartFullFilePreload()) {
            if (playbackActive || isTimelinePlaybackActive()) {
              recordPlaybackBackgroundWork('full-file-video-preload', 'deferred', {
                reason: 'timeline-playback-active',
                count: queue.size,
              })
            }
            scheduleFullFilePreload(PLAYBACK_RETRY_DELAY_MS)
            return
          }
          if (!deadline.didTimeout && deadline.timeRemaining() < 8) {
            scheduleFullFilePreload(PLAYBACK_RETRY_DELAY_MS)
            return
          }
          void runNextFullFilePreload()
        },
        { timeout: 10_000 },
      )
      return
    }

    void runNextFullFilePreload()
  }, delayMs)
}

function requeue(item: QueuedFullFileVideoPreload): boolean {
  if (item.attempts >= MAX_ATTEMPTS) return false
  queue.set(getVideoCacheKeyForSource(item.source), item)
  return true
}

async function runNextFullFilePreload(): Promise<void> {
  if (inFlight || !canStartFullFilePreload()) {
    if (playbackActive || isTimelinePlaybackActive()) {
      recordPlaybackBackgroundWork('full-file-video-preload', 'deferred', {
        reason: 'timeline-playback-active',
        count: queue.size,
      })
    }
    scheduleFullFilePreload(PLAYBACK_RETRY_DELAY_MS)
    return
  }

  const next = queue.values().next().value
  if (!next) return

  const cacheKey = getVideoCacheKeyForSource(next.source)
  queue.delete(cacheKey)

  // Claim `inFlight` BEFORE the first await so concurrent timers that fire
  // during the `hasExactCachedVideo` yield can't dequeue a second item and
  // start a parallel untracked download.
  const controller = new AbortController()
  inFlight = { item: next, controller }
  let nextScheduleDelayMs: number | null = null

  try {
    if (await hasExactCachedVideoForSource(next.source)) {
      nextScheduleDelayMs = 0
      return
    }

    next.attempts++
    const result = await fillExactVideoCacheForSource(next.source, { signal: controller.signal })

    if (result.status === 'failed' && result.errorName === 'AbortError') {
      // Playback-induced aborts must not consume retry budget — otherwise two
      // play/pause cycles can permanently drop a queued preload without any
      // real fetch failure. Roll back the attempt and requeue.
      next.attempts--
      if (requeue(next)) nextScheduleDelayMs = PLAYBACK_RETRY_DELAY_MS
      else if (queue.size > 0) nextScheduleDelayMs = 0
      return
    }

    if (result.status === 'failed' || result.status === 'quota-exceeded') {
      if (requeue(next)) nextScheduleDelayMs = FAILURE_RETRY_DELAY_MS
      else if (queue.size > 0) nextScheduleDelayMs = 0
      return
    }

    nextScheduleDelayMs = 0
  } finally {
    inFlight = null
    if (nextScheduleDelayMs !== null) scheduleFullFilePreload(nextScheduleDelayMs)
  }
}

export function queueFullFileVideoPreload(
  videoId: string,
  url: string,
  reason: FullFileVideoPreloadReason = 'manual',
): void {
  if (!isBrowser() || !videoId || !url) return

  const source = createVideoPlaybackSourceFromUrl(videoId, url)
  const cacheKey = getVideoCacheKeyForSource(source)
  if (queue.has(cacheKey)) return
  if (inFlight && getVideoCacheKeyForSource(inFlight.item.source) === cacheKey) return

  queue.set(cacheKey, {
    source,
    reason,
    queuedAt: now(),
    attempts: 0,
  })
  if (playbackActive || isTimelinePlaybackActive()) {
    recordPlaybackBackgroundWork('full-file-video-preload', 'deferred', { reason: 'queued-during-playback' })
  }
  scheduleFullFilePreload()
}

export function setFullFileVideoPreloadPlaybackActive(active: boolean): void {
  if (playbackActive === active) return
  playbackActive = active
  if (active) {
    if (queue.size > 0) {
      recordPlaybackBackgroundWork('full-file-video-preload', 'deferred', {
        reason: 'playback-started',
        count: queue.size,
      })
    }
    if (inFlight) {
      recordPlaybackBackgroundWork('full-file-video-preload', 'skipped', { reason: 'aborted-for-playback' })
    }
    clearScheduledWork()
    inFlight?.controller.abort()
    return
  }
  if (queue.size > 0) {
    recordPlaybackBackgroundWork('full-file-video-preload', 'resumed', {
      reason: 'playback-stopped',
      count: queue.size,
    })
  }
  scheduleFullFilePreload(PLAYBACK_RETRY_DELAY_MS)
}

function visibilityChangeHandler(): void {
  if (typeof document === 'undefined') return
  if (document.visibilityState === 'hidden') {
    clearScheduledWork()
    inFlight?.controller.abort()
    return
  }
  scheduleFullFilePreload(PLAYBACK_RETRY_DELAY_MS)
}

let visibilityListenerInstalled = false

function installVisibilityListener(): void {
  if (visibilityListenerInstalled || typeof document === 'undefined') return
  document.addEventListener('visibilitychange', visibilityChangeHandler)
  visibilityListenerInstalled = true
}

function uninstallVisibilityListener(): void {
  if (!visibilityListenerInstalled || typeof document === 'undefined') return
  document.removeEventListener('visibilitychange', visibilityChangeHandler)
  visibilityListenerInstalled = false
}

export function __resetFullFileVideoPreloadCoordinatorForTests(): void {
  clearScheduledWork()
  inFlight?.controller.abort()
  inFlight = null
  queue.clear()
  playbackActive = false
  // The module-level visibility listener mutates `inFlight` and re-schedules
  // preloads, which would survive a reset and leak side effects across tests.
  // Tests that need the listener must call `installVisibilityListener()`.
  uninstallVisibilityListener()
}

installVisibilityListener()
subscribeTimelinePlaybackActivity(setFullFileVideoPreloadPlaybackActive)
